import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Download, FileArchive, FileSpreadsheet, Trash2, Upload, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { api, selectedCompanyId } from "../lib/api";
import { formatBytes, formatTimestamp } from "../lib/format";
import { todayBucharestIso } from "../lib/intervals";
import { cn, formatMw, formatPrice, signClass } from "../lib/utils";
import { desktopOnlyRoute } from "../components/ui/mobile-availability-page";
import { anreMonthlyInputState } from "../features/reports/anre-monthly";

export const Route = createFileRoute("/reports")({
  component: desktopOnlyRoute(ReportsPage),
});

interface Portfolio {
  id: string;
  brokerCode: string;
  shortName: string | null;
  market: "DA" | "ID";
  ownerEmail: string | null;
  ownerDisplayName: string | null;
}

interface DailyGridRow {
  qhIndex: number;
  daBuyMw: number;
  daBuyVwapEur: number | null;
  daBuyVwapRon: number | null;
  daSellMw: number;
  daSellVwapEur: number | null;
  daSellVwapRon: number | null;
  daPriceEur: number;
  daPriceRon: number;
  daBaseMw: number;
  idBuyMw: number;
  idBuyVwapEur: number | null;
  idSellMw: number;
  idSellVwapEur: number | null;
  netMw: number;
  netMwh: number;
  bmPriceSource: "final" | "estimated" | "missing";
  bmAppliedPriceEur: number | null;
  bmPositionPriceEur: number | null;
  bmSettlementEur: number | null;
  bmPnlEur: number | null;
  bmDataStatus: "final" | "estimated" | "partial" | "missing";
}

interface MarketLegSummary {
  mwh: number;
  vwapEur: number | null;
  vwapRon: number | null;
  valueEur: number | null;
  valueRon: number | null;
}

interface MarketBreakdownSummary {
  buy: MarketLegSummary;
  sell: MarketLegSummary;
  netMwh: number;
  cashflowEur: number | null;
  cashflowRon: number | null;
}

interface BalancingLegSummary {
  mwh: number;
  avgPriceEur: number | null;
  avgPositionPriceEur: number | null;
  cashflowEur: number | null;
  pnlEur: number | null;
  intervals: number;
}

interface BalancingBreakdownSummary {
  long: BalancingLegSummary;
  short: BalancingLegSummary;
  netMwh: number;
  cashflowEur: number | null;
  pnlEur: number | null;
}

interface DailyReport {
  generatedAt: string;
  deliveryDate: string;
  fx: { rate: number; source: string; asOfDate: string | null };
  portfolios: { da: string | null; id: string | null };
  coverage: {
    daSource: "schedule" | "brmda_trades" | "none";
    daRows: number;
    idRows: number;
    balancingRows: number;
    finalBalancingRows: number;
    estimatedBalancingRows: number;
    fxAvailable: boolean;
  };
  summary: {
    da: MarketBreakdownSummary;
    id: MarketBreakdownSummary;
    balancing: BalancingBreakdownSummary;
    daMwh: number;
    daCashRon: number;
    daCashEur: number | null;
    idBoughtMwh: number;
    idSoldMwh: number;
    idNetMwh: number;
    idCashEur: number;
    netMwh: number;
    openQhCount: number;
    flatQhCount: number;
    bmExposureMwh: number;
    bmCashflowEur: number | null;
    bmPnlEur: number | null;
  };
  grid: DailyGridRow[];
}

interface AnreLeg {
  quantityMWh: number;
  valueRon: number;
  priceRonMWh: number;
}

interface SettlementComponent extends AnreLeg {
  vatRon: number;
  totalRon: number;
}

interface SettlementBreakdown {
  buyPositive: SettlementComponent;
  sellPositive: SettlementComponent;
  buyNegative: SettlementComponent;
  sellNegative: SettlementComponent;
}

interface AnreSummary {
  participant: string;
  monthLabel: string;
  year: number;
  centralizers?: { brmda: boolean; brmid: boolean };
  brmda: { buy: AnreLeg; sell: AnreLeg; breakdown?: SettlementBreakdown };
  brmid: { buy: AnreLeg; sell: AnreLeg; breakdown?: SettlementBreakdown; mappedTo: "IDCT" };
}

interface AnreComparisonRow {
  market: "BRMDA" | "BRMID";
  side: "buy" | "sell";
  label: string;
  source: "dam_schedule" | "brmda_internal" | "brmda_trades" | "brmid_trades" | "none";
  report: AnreLeg;
  internal: AnreLeg | null;
  diff: {
    quantityMWh: number | null;
    valueRon: number | null;
    priceRonMWh: number | null;
  };
  status: "ok" | "info" | "warning";
  message: string | null;
}

interface AnreComparison {
  status: "ok" | "info" | "warning";
  participant: {
    report: string;
    internalCompany: string;
    status: "ok" | "warning";
  };
  rows: AnreComparisonRow[];
}

interface SavedAnreReport {
  id: string;
  participant: string;
  monthLabel: string;
  year: number;
  reportMonth: number;
  resultFilename: string;
  resultSizeBytes: number;
  templateFilename: string;
  templateSizeBytes: number;
  brmdaFilename: string | null;
  brmdaSizeBytes: number | null;
  brmidFilename: string | null;
  brmidSizeBytes: number | null;
  summary: AnreSummary;
  comparison: AnreComparison;
  createdAt: string;
  createdByEmail: string | null;
  createdByDisplayName: string | null;
}

interface SavedAnreTemplate {
  filename: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedByEmail: string | null;
  uploadedByDisplayName: string | null;
}

type ReportDocumentType = "invoice" | "settlement" | "centralizer" | "contract" | "supporting" | "other";
type ReportDocumentMarket = "BRMDA" | "BRMID" | "BALANCING" | "OTHER";
type ReportDocumentFlow = "payable" | "receivable" | "neutral";
type ReportDocumentAmountBasis = "without_vat" | "with_vat" | "unknown";

interface SavedReportDocument {
  id: string;
  documentType: ReportDocumentType;
  reportMonth: string | null;
  market: ReportDocumentMarket | null;
  flow: ReportDocumentFlow;
  amountBasis: ReportDocumentAmountBasis;
  counterparty: string | null;
  invoiceNumber: string | null;
  issueDate: string | null;
  dueDate: string | null;
  amount: number | null;
  currency: string | null;
  filename: string;
  contentType: string;
  sha256: string;
  sizeBytes: number;
  notes: string | null;
  createdAt: string;
  uploadedByEmail: string | null;
  uploadedByDisplayName: string | null;
}

interface ReportDocumentExtractedFields {
  documentType?: ReportDocumentType;
  market?: ReportDocumentMarket;
  flow?: ReportDocumentFlow;
  amountBasis?: ReportDocumentAmountBasis;
  counterparty?: string;
  invoiceNumber?: string;
  issueDate?: string;
  dueDate?: string;
  amount?: number;
  currency?: string;
  notes?: string;
}

interface ReportDocumentExtraction {
  filename: string;
  contentType: string;
  source: "xml" | "spreadsheet" | "pdf" | "text" | "filename" | "unsupported";
  confidence: "high" | "medium" | "low";
  fields: ReportDocumentExtractedFields;
  warnings: string[];
}

interface ReportDocumentCheckRow {
  market: "BRMDA" | "BRMID";
  flow: "payable" | "receivable";
  label: string;
  uploadedAmountRon: number | null;
  reportAmountRon: number | null;
  diffRon: number | null;
  documentCount: number;
  status: "ok" | "info" | "warning";
  message: string | null;
}

interface ReportDocumentCrossCheck {
  reportMonth: string;
  status: "ok" | "info" | "warning";
  anreReport: {
    id: string;
    participant: string;
    monthLabel: string;
    year: number;
    createdAt: string;
  } | null;
  rows: ReportDocumentCheckRow[];
  unmatchedDocuments: number;
}

type ReportType = "daily" | "anreMonthly";

function ReportsPage() {
  const [reportType, setReportType] = useState<ReportType>("daily");
  const [deliveryDate, setDeliveryDate] = useState(() => todayBucharestIso());
  const [portfolioDa, setPortfolioDa] = useState("");
  const [portfolioId, setPortfolioId] = useState("");
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const portfoliosQ = useQuery({
    queryKey: ["portfolios"],
    queryFn: () => api<{ portfolios: Portfolio[] }>("/portfolios"),
  });

  const daPortfolios = useMemo(
    () => (portfoliosQ.data?.portfolios ?? []).filter((p) => p.market === "DA"),
    [portfoliosQ.data]
  );
  const idPortfolios = useMemo(
    () => (portfoliosQ.data?.portfolios ?? []).filter((p) => p.market === "ID"),
    [portfoliosQ.data]
  );

  const params = useMemo(() => {
    const p = new URLSearchParams({ deliveryDate });
    if (portfolioDa) p.set("portfolioDa", portfolioDa);
    if (portfolioId) p.set("portfolioId", portfolioId);
    return p;
  }, [deliveryDate, portfolioDa, portfolioId]);

  const reportQ = useQuery({
    queryKey: ["reports", "daily", params.toString()],
    queryFn: () => api<DailyReport>(`/reports/daily?${params.toString()}`),
    enabled: reportType === "daily",
  });

  async function exportXlsx() {
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await fetch(`/api/reports/daily.xlsx?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? res.statusText);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `daily-trading-report-${deliveryDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError((err as Error).message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Report">
          <select
            className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            value={reportType}
            onChange={(e) => setReportType(e.target.value as ReportType)}
          >
            <option value="daily">Daily trading</option>
            <option value="anreMonthly">Monthly report - ANRE</option>
          </select>
        </Field>
        {reportType === "daily" && (
          <>
            <Field label="Delivery date">
              <input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                className="bg-transparent border border-border rounded px-2 py-1 text-sm"
              />
            </Field>
            <Field label="DA portfolio">
              <PortfolioSelect
                value={portfolioDa}
                onChange={setPortfolioDa}
                options={daPortfolios}
                placeholder="all DA"
              />
            </Field>
            <Field label="ID portfolio">
              <PortfolioSelect
                value={portfolioId}
                onChange={setPortfolioId}
                options={idPortfolios}
                placeholder="all ID"
              />
            </Field>
            <button
              type="button"
              onClick={exportXlsx}
              disabled={downloading || reportQ.isLoading}
              className="border border-accent/60 text-accent rounded px-3 py-1.5 text-sm disabled:opacity-50"
            >
              {downloading ? "Exporting..." : "Export XLSX"}
            </button>
          </>
        )}
      </div>

      {downloadError && <div className="text-negRed text-xs">{downloadError}</div>}
      {reportType === "anreMonthly" && <AnreMonthlyFill />}
      {reportType === "daily" && reportQ.isLoading && <div className="text-muted text-sm">Loading...</div>}
      {reportType === "daily" && reportQ.isError && (
        <div className="text-negRed text-sm">{(reportQ.error as Error).message}</div>
      )}
      {reportType === "daily" && reportQ.data && <DailyReportView report={reportQ.data} />}
    </div>
  );
}

function AnreMonthlyFill() {
  const queryClient = useQueryClient();
  const [template, setTemplate] = useState<File | null>(null);
  const [brmda, setBrmda] = useState<File | null>(null);
  const [brmid, setBrmid] = useState<File | null>(null);
  const [dropActive, setDropActive] = useState(false);
  const panelDragDepthRef = useRef(0);
  const [filling, setFilling] = useState(false);
  const [downloadingArtifact, setDownloadingArtifact] = useState<string | null>(null);
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [latest, setLatest] = useState<SavedAnreReport | null>(null);

  const savedReportsQ = useQuery({
    queryKey: ["reports", "anreMonthly"],
    queryFn: () => api<{ reports: SavedAnreReport[]; template: SavedAnreTemplate | null }>("/reports/anre/monthly"),
  });

  const defaultTemplate = savedReportsQ.data?.template ?? null;
  const hasTemplateForRun = Boolean(template || defaultTemplate);
  const inputState = anreMonthlyInputState({
    hasTemplate: hasTemplateForRun,
    hasBrmda: Boolean(brmda),
    hasBrmid: Boolean(brmid),
    filling,
  });

  function assignDroppedFiles(files: File[]) {
    const unknown: string[] = [];
    let assigned = 0;
    setError(null);
    for (const file of files) {
      const slot = classifyAnreDropFile(file);
      if (slot === "template") {
        if (fileMatchesAccept(file, ".xls")) {
          setTemplate(file);
          assigned++;
        } else {
          unknown.push(file.name);
        }
      } else if (slot === "brmda") {
        if (fileMatchesAccept(file, ".xlsx,.xls")) {
          setBrmda(file);
          assigned++;
        } else {
          unknown.push(file.name);
        }
      } else if (slot === "brmid") {
        if (fileMatchesAccept(file, ".xlsx,.xls")) {
          setBrmid(file);
          assigned++;
        } else {
          unknown.push(file.name);
        }
      } else {
        unknown.push(file.name);
      }
    }
    if (assigned === 0 && unknown.length > 0) {
      setError("Dropped files could not be matched to ANRE template, BRMDA, or BRMID.");
    } else if (unknown.length > 0) {
      setError(`Some dropped files were not used: ${unknown.join(", ")}`);
    }
  }

  function handlePanelDrop(e: React.DragEvent<HTMLElement>) {
    e.preventDefault();
    resetPanelDragState();
    assignDroppedFiles(Array.from(e.dataTransfer.files));
  }

  function resetPanelDragState() {
    panelDragDepthRef.current = 0;
    setDropActive(false);
  }

  function handlePanelDragEnter(e: React.DragEvent<HTMLElement>) {
    e.preventDefault();
    if (!isFileDrag(e.dataTransfer)) return;
    panelDragDepthRef.current += 1;
    setDropActive(true);
  }

  function handlePanelDragOver(e: React.DragEvent<HTMLElement>) {
    e.preventDefault();
    if (!isFileDrag(e.dataTransfer)) return;
    e.dataTransfer.dropEffect = "copy";
    setDropActive(true);
  }

  function handlePanelDragLeave(e: React.DragEvent<HTMLElement>) {
    e.preventDefault();
    if (!isFileDrag(e.dataTransfer)) return;
    panelDragDepthRef.current = Math.max(0, panelDragDepthRef.current - 1);
    if (panelDragDepthRef.current === 0) setDropActive(false);
  }

  async function fillReport() {
    if (!brmda && !brmid) return;
    if (!hasTemplateForRun) {
      setError("Upload an ANRE template once; after that the saved default template can be reused.");
      return;
    }
    setFilling(true);
    setError(null);
    setLatest(null);
    try {
      const fd = new FormData();
      if (template) fd.append("template", template);
      if (brmda) fd.append("brmda", brmda);
      if (brmid) fd.append("brmid", brmid);
      const res = await fetch("/api/reports/anre/monthly.xls", {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? res.statusText);
      }
      const nextSaved = parseBase64JsonHeader<SavedAnreReport>(res.headers.get("x-anre-report"));
      const nextSummary = parseBase64JsonHeader<AnreSummary>(res.headers.get("x-anre-summary"));
      if (nextSaved) setLatest(nextSaved);
      else if (nextSummary) setLatest(summaryOnlySavedReport(nextSummary));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromDisposition(res.headers.get("content-disposition")) ?? "anre-monthly-filled.xls";
      a.click();
      URL.revokeObjectURL(url);
      await queryClient.invalidateQueries({ queryKey: ["reports", "anreMonthly"] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setFilling(false);
    }
  }

  async function downloadDefaultTemplate() {
    setDownloadingArtifact("default-template");
    setError(null);
    try {
      const res = await fetch("/api/reports/anre/monthly/template/download", {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? res.statusText);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromDisposition(res.headers.get("content-disposition")) ?? "anre-template.xls";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDownloadingArtifact(null);
    }
  }

  async function downloadArtifact(report: SavedAnreReport, artifact: "report" | "template" | "brmda" | "brmid") {
    const key = `${report.id}:${artifact}`;
    setDownloadingArtifact(key);
    setError(null);
    try {
      const res = await fetch(`/api/reports/anre/monthly/${report.id}/download?artifact=${artifact}`, {
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? res.statusText);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromDisposition(res.headers.get("content-disposition")) ?? report.resultFilename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDownloadingArtifact(null);
    }
  }

  async function deleteReport(report: SavedAnreReport) {
    const ok = window.confirm(
      `Delete saved ANRE report for ${report.participant} - ${report.monthLabel} ${report.year}?`
    );
    if (!ok) return;

    setDeletingReportId(report.id);
    setError(null);
    try {
      const res = await fetch(`/api/reports/anre/monthly/${report.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? res.statusText);
      }
      if (latest?.id === report.id) setLatest(null);
      await queryClient.invalidateQueries({ queryKey: ["reports", "anreMonthly"] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeletingReportId(null);
    }
  }

  return (
    <>
      <section
        className={cn(
          "border border-border/60 rounded p-4 space-y-4 transition-all duration-150",
          dropActive && "border-accent bg-accent/10 ring-2 ring-accent/30"
        )}
        onDragEnter={handlePanelDragEnter}
        onDragOver={handlePanelDragOver}
        onDragLeave={handlePanelDragLeave}
        onDrop={handlePanelDrop}
      >
        <div className="flex flex-wrap items-end gap-3">
          <FileField
            label="ANRE template"
            file={template}
            onChange={setTemplate}
            onRejected={(file) => setError(`${file.name} is not an .xls ANRE template.`)}
            accept=".xls"
            panelActive={dropActive}
            onDropComplete={resetPanelDragState}
          />
          <FileField
            label="BRMDA centralizer"
            file={brmda}
            onChange={setBrmda}
            onRejected={(file) => setError(`${file.name} is not a supported BRMDA centralizer.`)}
            accept=".xlsx,.xls"
            panelActive={dropActive}
            onDropComplete={resetPanelDragState}
          />
          <FileField
            label="BRMID centralizer"
            file={brmid}
            onChange={setBrmid}
            onRejected={(file) => setError(`${file.name} is not a supported BRMID centralizer.`)}
            accept=".xlsx,.xls"
            panelActive={dropActive}
            onDropComplete={resetPanelDragState}
          />
          <button
            type="button"
            onClick={fillReport}
            title={inputState.submitTitle}
            disabled={!inputState.canSubmit}
            className="inline-flex items-center gap-2 border border-accent/60 text-accent rounded px-3 py-1.5 text-sm disabled:opacity-50"
          >
            <FileSpreadsheet size={15} />
            {filling ? "Filling..." : "Fill ANRE XLS"}
          </button>
        </div>
        <DefaultTemplateStatus
          template={defaultTemplate}
          uploadingOverride={Boolean(template)}
          loading={savedReportsQ.isLoading}
          downloading={downloadingArtifact === "default-template"}
          onDownload={downloadDefaultTemplate}
        />
        {inputState.warning && (
          <div className="flex items-center gap-1.5 rounded border border-warnOrange/40 bg-warnOrange/10 px-3 py-2 text-xs text-warnOrange">
            <AlertTriangle size={14} className="shrink-0" />
            {inputState.warning}
          </div>
        )}
        {error && <div className="text-negRed text-xs">{error}</div>}
        {latest && <AnreReportDetails report={latest} />}
        {savedReportsQ.isLoading && <div className="text-muted text-sm">Loading...</div>}
        {savedReportsQ.isError && <div className="text-negRed text-sm">{(savedReportsQ.error as Error).message}</div>}
        {savedReportsQ.data && (
          <SavedAnreReportsList
            reports={savedReportsQ.data.reports}
            downloadingArtifact={downloadingArtifact}
            deletingReportId={deletingReportId}
            onDownload={downloadArtifact}
            onDelete={deleteReport}
          />
        )}
      </section>
      <ReportDocumentArchive />
    </>
  );
}

const DOCUMENT_FILE_ACCEPT = ".pdf,.xml,.xlsx,.xls,.csv,.txt,.doc,.docx,.png,.jpg,.jpeg";
const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

function ReportDocumentArchive() {
  const queryClient = useQueryClient();
  const initialPeriod = useMemo(() => defaultReportPeriod(), []);
  const [reportMonthNumber, setReportMonthNumber] = useState(initialPeriod.month);
  const [reportYear, setReportYear] = useState(initialPeriod.year);
  const [filterType, setFilterType] = useState<"" | ReportDocumentType>("");
  const [search, setSearch] = useState("");
  const [documentType, setDocumentType] = useState<ReportDocumentType>("invoice");
  const [market, setMarket] = useState<"" | ReportDocumentMarket>("");
  const [flow, setFlow] = useState<ReportDocumentFlow>("neutral");
  const [amountBasis, setAmountBasis] = useState<ReportDocumentAmountBasis>("unknown");
  const [counterparty, setCounterparty] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [extractions, setExtractions] = useState<ReportDocumentExtraction[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const extractionRunRef = useRef(0);
  const reportMonth = useMemo(
    () => reportMonthFromParts(reportYear, reportMonthNumber),
    [reportYear, reportMonthNumber]
  );
  const yearOptions = useMemo(() => reportYearOptions(reportYear), [reportYear]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (reportMonth) p.set("reportMonth", reportMonth);
    if (filterType) p.set("documentType", filterType);
    if (search.trim()) p.set("q", search.trim());
    return p;
  }, [reportMonth, filterType, search]);

  const documentsQ = useQuery({
    queryKey: ["reports", "documents", params.toString()],
    queryFn: () =>
      api<{ documents: SavedReportDocument[]; crossCheck: ReportDocumentCrossCheck | null }>(
        `/reports/documents?${params.toString()}`
      ),
  });

  const selectedSize = files.reduce((sum, file) => sum + file.size, 0);
  const canUpload = files.length > 0 && !uploading && !extracting;

  function applyFiles(nextFiles: File[]) {
    setFiles(nextFiles);
    setExtractions([]);
    setError(null);
    void extractFiles(nextFiles);
  }

  async function extractFiles(nextFiles: File[]) {
    const run = extractionRunRef.current + 1;
    extractionRunRef.current = run;
    if (nextFiles.length === 0) {
      setExtracting(false);
      return;
    }
    setExtracting(true);
    try {
      const fd = new FormData();
      nextFiles.forEach((file) => fd.append("files", file));
      const res = await fetch("/api/reports/documents/extract", {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: activeCompanyHeaders(),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error ?? res.statusText);
      if (extractionRunRef.current !== run) return;
      const nextExtractions = (body?.extractions ?? []) as ReportDocumentExtraction[];
      setExtractions(nextExtractions);
      if (nextExtractions.length === 1 && nextExtractions[0]) applyExtractedFields(nextExtractions[0].fields);
    } catch (err) {
      if (extractionRunRef.current === run) setError((err as Error).message);
    } finally {
      if (extractionRunRef.current === run) setExtracting(false);
    }
  }

  function applyExtractedFields(fields: ReportDocumentExtractedFields) {
    if (fields.documentType) setDocumentType(fields.documentType);
    if (fields.market) setMarket(fields.market);
    if (fields.flow) setFlow(fields.flow);
    if (fields.amountBasis) setAmountBasis(fields.amountBasis);
    if (fields.counterparty) setCounterparty(fields.counterparty);
    if (fields.invoiceNumber) setInvoiceNumber(fields.invoiceNumber);
    if (fields.issueDate) setIssueDate(fields.issueDate);
    if (fields.dueDate) setDueDate(fields.dueDate);
    if (fields.amount !== undefined) setAmount(String(fields.amount));
    if (fields.currency) setCurrency(fields.currency);
    if (fields.notes) setNotes(fields.notes);
  }

  function handleDrop(e: React.DragEvent<HTMLElement>) {
    e.preventDefault();
    setDragActive(false);
    applyFiles(Array.from(e.dataTransfer.files));
  }

  async function uploadDocuments() {
    if (files.length === 0) return;
    if (files.length > 1 && amount.trim()) {
      setError("Upload one amount-bearing invoice at a time; bulk files can be archived without amount.");
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("documentType", documentType);
      fd.append("reportMonth", reportMonth);
      if (market) fd.append("market", market);
      fd.append("flow", flow);
      fd.append("amountBasis", amountBasis);
      fd.append("counterparty", counterparty);
      fd.append("invoiceNumber", invoiceNumber);
      fd.append("issueDate", issueDate);
      fd.append("dueDate", dueDate);
      fd.append("amount", amount);
      fd.append("currency", currency);
      fd.append("notes", notes);
      fd.append("autoExtract", "true");
      files.forEach((file) => fd.append("files", file));
      const res = await fetch("/api/reports/documents", {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: activeCompanyHeaders(),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.error ?? res.statusText);
      setFiles([]);
      setExtractions([]);
      setInvoiceNumber("");
      setAmount("");
      await queryClient.invalidateQueries({ queryKey: ["reports", "documents"] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  }

  async function downloadDocument(doc: SavedReportDocument) {
    setDownloadingId(doc.id);
    setError(null);
    try {
      const res = await fetch(`/api/reports/documents/${doc.id}/download`, {
        credentials: "include",
        headers: activeCompanyHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? res.statusText);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromDisposition(res.headers.get("content-disposition")) ?? doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDownloadingId(null);
    }
  }

  async function deleteDocument(document: SavedReportDocument) {
    const ok = window.confirm(`Delete ${document.filename}?`);
    if (!ok) return;
    setDeletingId(document.id);
    setError(null);
    try {
      await api<void>(`/reports/documents/${document.id}`, { method: "DELETE" });
      await queryClient.invalidateQueries({ queryKey: ["reports", "documents"] });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <section className="space-y-4">
      <div className="border border-border/60 rounded p-4 space-y-4">
        <div>
          <div className="text-xs text-muted uppercase tracking-wider">Monthly report documents</div>
          <div className="text-sm font-semibold">{reportMonthLabel(reportMonth)}</div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Month">
            <select
              value={reportMonthNumber}
              onChange={(e) => setReportMonthNumber(Number(e.target.value))}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            >
              {MONTH_OPTIONS.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Year">
            <select
              value={reportYear}
              onChange={(e) => setReportYear(Number(e.target.value))}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div
          className={cn(
            "flex flex-wrap items-center gap-3 rounded border border-dashed border-border px-3 py-3 transition-all duration-150",
            dragActive && "border-accent bg-accent/15 ring-2 ring-accent/30"
          )}
          onDragEnter={(e) => {
            e.preventDefault();
            if (isFileDrag(e.dataTransfer)) setDragActive(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (isFileDrag(e.dataTransfer)) e.dataTransfer.dropEffect = "copy";
          }}
          onDragLeave={(e) => {
            if (isFileDrag(e.dataTransfer)) setDragActive(false);
          }}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={DOCUMENT_FILE_ACCEPT}
            onChange={(e) => applyFiles(Array.from(e.target.files ?? []))}
            className="sr-only"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-2 rounded border border-border px-3 py-1.5 text-sm hover:border-accent hover:text-accent"
          >
            <Upload size={15} />
            Files
          </button>
          <div className="min-w-0 flex-1 text-xs">
            <div className="truncate font-mono text-fg">
              {files.length ? files.map((file) => file.name).join(" / ") : "No files selected"}
            </div>
            <div className="text-muted">{files.length ? `${files.length} file(s) · ${formatBytes(selectedSize)}` : "PDF, XML, Excel, Word, image, CSV, or text"}</div>
          </div>
          <button
            type="button"
            onClick={uploadDocuments}
            disabled={!canUpload}
            className="inline-flex items-center gap-2 rounded border border-accent/60 px-3 py-1.5 text-sm text-accent disabled:opacity-50"
          >
            <FileArchive size={15} />
            {uploading ? "Uploading..." : extracting ? "Reading..." : "Upload"}
          </button>
        </div>

        {(extracting || extractions.length > 0) && (
          <ReportDocumentExtractionPanel extracting={extracting} extractions={extractions} />
        )}

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
          <Field label="Type">
            <DocumentTypeSelect value={documentType} onChange={setDocumentType} />
          </Field>
          <Field label="Market">
            <DocumentMarketSelect value={market} onChange={setMarket} />
          </Field>
          <Field label="Flow">
            <select
              value={flow}
              onChange={(e) => setFlow(e.target.value as ReportDocumentFlow)}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            >
              <option value="payable">Payable</option>
              <option value="receivable">Receivable</option>
              <option value="neutral">Neutral</option>
            </select>
          </Field>
          <Field label="Counterparty">
            <input
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Invoice">
            <input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Issue date">
            <input
              type="date"
              value={issueDate}
              onChange={(e) => setIssueDate(e.target.value)}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Due date">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Amount">
            <input
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Currency">
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm uppercase"
            />
          </Field>
          <Field label="Basis">
            <select
              value={amountBasis}
              onChange={(e) => setAmountBasis(e.target.value as ReportDocumentAmountBasis)}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            >
              <option value="unknown">Unknown</option>
              <option value="without_vat">Without VAT</option>
              <option value="with_vat">With VAT</option>
            </select>
          </Field>
          <Field label="Notes">
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            />
          </Field>
        </div>

        <div className="flex flex-wrap items-end gap-3 border-t border-border/40 pt-3">
          <Field label="Show type">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as "" | ReportDocumentType)}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm"
            >
              <option value="">All</option>
              <DocumentTypeOptions />
            </select>
          </Field>
          <Field label="Search">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent border border-border rounded px-2 py-1 text-sm min-w-[16rem]"
            />
          </Field>
        </div>

        {error && <div className="text-negRed text-xs">{error}</div>}
      </div>

      {documentsQ.isLoading && <div className="text-muted text-sm">Loading...</div>}
      {documentsQ.isError && <div className="text-negRed text-sm">{(documentsQ.error as Error).message}</div>}
      {documentsQ.data?.crossCheck && <ReportDocumentCrossCheckPanel check={documentsQ.data.crossCheck} />}
      {documentsQ.data && (
        <ReportDocumentList
          documents={documentsQ.data.documents}
          downloadingId={downloadingId}
          deletingId={deletingId}
          onDownload={downloadDocument}
          onDelete={deleteDocument}
        />
      )}
    </section>
  );
}

function ReportDocumentExtractionPanel({
  extracting,
  extractions,
}: {
  extracting: boolean;
  extractions: ReportDocumentExtraction[];
}) {
  return (
    <section className="overflow-auto border border-border/40 rounded">
      <table className="min-w-[52rem] w-full text-xs">
        <thead className="text-muted">
          <tr>
            <Th>Read</Th>
            <Th>File</Th>
            <Th>Detected details</Th>
            <Th>Warnings</Th>
          </tr>
        </thead>
        <tbody>
          {extracting && extractions.length === 0 && (
            <tr className="border-t border-border/30">
              <Td>Reading...</Td>
              <Td colSpan={3}>Extracting invoice details</Td>
            </tr>
          )}
          {extractions.map((extraction) => (
            <tr key={extraction.filename} className="border-t border-border/30">
              <Td>
                <DocumentExtractionBadge confidence={extraction.confidence} />
              </Td>
              <Td>
                <StackedValue primary={extraction.filename} secondary={documentExtractionSourceLabel(extraction.source)} />
              </Td>
              <Td>{documentExtractionSummary(extraction.fields)}</Td>
              <Td className={extraction.warnings.length ? "text-warnOrange" : "text-muted"}>
                {extraction.warnings.length ? extraction.warnings.join(" ") : "—"}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function DocumentExtractionBadge({ confidence }: { confidence: ReportDocumentExtraction["confidence"] }) {
  const ok = confidence === "high";
  const medium = confidence === "medium";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px]",
        ok
          ? "border-posGreen/40 bg-posGreen/10 text-posGreen"
          : medium
          ? "border-accent/40 bg-accent/10 text-accent"
          : "border-warnOrange/50 bg-warnOrange/10 text-warnOrange"
      )}
    >
      {ok ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
      {ok ? "Read" : medium ? "Partial" : "Low"}
    </span>
  );
}

function ReportDocumentCrossCheckPanel({ check }: { check: ReportDocumentCrossCheck }) {
  return (
    <section className="border border-border/60 rounded p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="text-xs text-muted uppercase tracking-wider">Invoice cross-check</div>
          <div className="text-sm font-semibold">
            {reportMonthLabel(check.reportMonth)}
            {check.anreReport ? ` · ${check.anreReport.participant}` : ""}
          </div>
          {check.anreReport && (
            <div className="text-xs text-muted">
              ANRE {check.anreReport.monthLabel} {check.anreReport.year} · {formatTimestamp(check.anreReport.createdAt)}
            </div>
          )}
        </div>
        <DocumentCheckBadge status={check.status} />
      </div>
      <div className="overflow-auto border border-border/40 rounded">
        <table className="min-w-[52rem] w-full font-mono text-[11px]">
          <thead className="text-muted">
            <tr>
              <Th>Check</Th>
              <Th className="text-right">Docs</Th>
              <Th className="text-right">Uploaded RON</Th>
              <Th className="text-right">ANRE RON</Th>
              <Th className="text-right">Diff</Th>
              <Th>Flag</Th>
            </tr>
          </thead>
          <tbody>
            {check.rows.map((row) => (
              <tr key={`${row.market}:${row.flow}`} className={cn("border-t border-border/30", documentCheckClass(row.status))}>
                <Td>{row.label}</Td>
                <Td className="text-right">{row.documentCount}</Td>
                <Td className="text-right">{moneyLabel(row.uploadedAmountRon, "RON")}</Td>
                <Td className="text-right">{moneyLabel(row.reportAmountRon, "RON")}</Td>
                <Td className={cn("text-right", diffClass(row.diffRon))}>{formatSignedNumber(row.diffRon)}</Td>
                <Td>{row.message ?? "OK"}</Td>
              </tr>
            ))}
            {check.unmatchedDocuments > 0 && (
              <tr className="border-t border-border/30 text-accent">
                <Td>Other amount-bearing documents</Td>
                <Td className="text-right">{check.unmatchedDocuments}</Td>
                <Td colSpan={4}>Outside BRMDA/BRMID payable/receivable checks</Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ReportDocumentList({
  documents,
  downloadingId,
  deletingId,
  onDownload,
  onDelete,
}: {
  documents: SavedReportDocument[];
  downloadingId: string | null;
  deletingId: string | null;
  onDownload: (document: SavedReportDocument) => void;
  onDelete: (document: SavedReportDocument) => void;
}) {
  if (documents.length === 0) return <div className="text-muted text-sm">No uploaded report documents.</div>;
  return (
    <section className="overflow-auto border border-border/60 rounded">
      <table className="min-w-[72rem] w-full text-xs">
        <thead className="text-muted">
          <tr>
            <Th>Uploaded</Th>
            <Th>Type</Th>
            <Th>Period</Th>
            <Th>Market</Th>
            <Th>Counterparty</Th>
            <Th>Invoice</Th>
            <Th className="text-right">Amount</Th>
            <Th>File</Th>
            <Th>Notes</Th>
            <Th className="text-right">Download</Th>
            <Th className="text-right">Delete</Th>
          </tr>
        </thead>
        <tbody>
          {documents.map((document) => (
            <tr key={document.id} className="border-t border-border/30">
              <Td>{formatTimestamp(document.createdAt)}</Td>
              <Td>{documentTypeLabel(document.documentType)}</Td>
              <Td>{reportMonthLabel(document.reportMonth)}</Td>
              <Td>
                {document.market ?? "—"} · {flowLabel(document.flow)}
              </Td>
              <Td>{document.counterparty ?? "—"}</Td>
              <Td>{document.invoiceNumber ?? "—"}</Td>
              <Td className="text-right">
                <StackedValue
                  primary={document.amount === null ? "—" : `${formatPrice(document.amount)} ${document.currency ?? ""}`.trim()}
                  secondary={amountBasisLabel(document.amountBasis)}
                />
              </Td>
              <Td>
                <StackedValue primary={document.filename} secondary={formatBytes(document.sizeBytes)} />
              </Td>
              <Td>{document.notes ?? "—"}</Td>
              <Td className="text-right">
                <button
                  type="button"
                  onClick={() => onDownload(document)}
                  disabled={downloadingId === document.id}
                  className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] hover:border-accent hover:text-accent disabled:opacity-50"
                >
                  <Download size={12} />
                  File
                </button>
              </Td>
              <Td className="text-right">
                <button
                  type="button"
                  title="Delete uploaded document"
                  aria-label={`Delete ${document.filename}`}
                  onClick={() => onDelete(document)}
                  disabled={deletingId === document.id}
                  className="inline-flex h-7 w-7 items-center justify-center rounded border border-border text-muted hover:border-negRed hover:text-negRed disabled:opacity-50"
                >
                  <Trash2 size={14} />
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function AnreReportDetails({ report }: { report: SavedAnreReport }) {
  const comparison = report.comparison;
  return (
    <div className="border-t border-border/40 pt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <div className="text-xs text-muted">
            {report.participant} · {report.monthLabel} {report.year} · BRMID mapped to {report.summary.brmid.mappedTo}
          </div>
          <div className="text-xs text-muted">
            Saved {formatTimestamp(report.createdAt)}
            {report.createdByDisplayName || report.createdByEmail ? ` · ${report.createdByDisplayName ?? report.createdByEmail}` : ""}
          </div>
        </div>
        <AnreStatusBadge status={comparison.status} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <AnreSettlementBreakdownTable summary={report.summary} />
        <AnreComparisonTable comparison={comparison} />
      </div>
    </div>
  );
}

function AnreComparisonTable({ comparison }: { comparison: AnreComparison }) {
  return (
    <div className="overflow-auto border border-border/40 rounded">
      <table className="font-mono text-[11px] min-w-[64rem] w-full">
        <thead className="text-muted">
          <tr>
            <Th>Internal check</Th>
            <Th>Source</Th>
            <Th className="text-right">Report MWh</Th>
            <Th className="text-right">Internal MWh</Th>
            <Th className="text-right">MWh diff</Th>
            <Th className="text-right">lei diff</Th>
            <Th>Flag</Th>
          </tr>
        </thead>
        <tbody>
          {comparison.participant.status === "warning" && (
            <tr className="border-t border-border/30 text-warnOrange">
              <Td>Participant</Td>
              <Td>company</Td>
              <Td colSpan={5}>
                {comparison.participant.report} / {comparison.participant.internalCompany}
              </Td>
            </tr>
          )}
          {comparison.rows.map((row) => (
            <tr key={`${row.market}:${row.side}`} className={cn("border-t border-border/30", comparisonStatusClass(row.status))}>
              <Td>{row.label}</Td>
              <Td>{sourceShortLabel(row.source)}</Td>
              <Td className="text-right">{formatMw(row.report.quantityMWh)}</Td>
              <Td className="text-right">{row.internal ? formatMw(row.internal.quantityMWh) : "—"}</Td>
              <Td className={cn("text-right", diffClass(row.diff.quantityMWh))}>{formatSignedNumber(row.diff.quantityMWh)}</Td>
              <Td className={cn("text-right", diffClass(row.diff.valueRon))}>{formatSignedNumber(row.diff.valueRon)}</Td>
              <Td className={comparisonStatusClass(row.status)}>
                {row.message ?? "OK"}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SavedAnreReportsList({
  reports,
  downloadingArtifact,
  deletingReportId,
  onDownload,
  onDelete,
}: {
  reports: SavedAnreReport[];
  downloadingArtifact: string | null;
  deletingReportId: string | null;
  onDownload: (report: SavedAnreReport, artifact: "report" | "template" | "brmda" | "brmid") => void;
  onDelete: (report: SavedAnreReport) => void;
}) {
  if (reports.length === 0) return <div className="text-muted text-sm">No saved ANRE monthly reports.</div>;
  return (
    <div className="overflow-auto border-t border-border/40 pt-3">
      <table className="min-w-[58rem] w-full text-xs">
        <thead className="text-muted">
          <tr>
            <Th>Saved</Th>
            <Th>Period</Th>
            <Th>Participant</Th>
            <Th>Status</Th>
            <Th>Files</Th>
            <Th className="text-right">Download</Th>
            <Th className="text-right">Delete</Th>
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => (
            <tr key={report.id} className="border-t border-border/30">
              <Td>{formatTimestamp(report.createdAt)}</Td>
              <Td>
                {report.monthLabel} {report.year}
              </Td>
              <Td>{report.participant}</Td>
              <Td>
                <AnreStatusBadge status={report.comparison.status} compact />
              </Td>
              <Td className="text-muted">
                {formatBytes(report.templateSizeBytes)} / {optionalFileSize(report.brmdaSizeBytes)} / {optionalFileSize(report.brmidSizeBytes)}
              </Td>
              <Td className="text-right">
                <div className="inline-flex flex-wrap justify-end gap-1.5">
                  {availableAnreArtifacts(report).map((artifact) => (
                    <button
                      key={artifact}
                      type="button"
                      onClick={() => onDownload(report, artifact)}
                      disabled={downloadingArtifact === `${report.id}:${artifact}`}
                      className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] hover:border-accent hover:text-accent disabled:opacity-50"
                    >
                      <Download size={12} />
                      {artifactLabel(artifact)}
                    </button>
                  ))}
                </div>
              </Td>
              <Td className="text-right">
                <button
                  type="button"
                  title="Delete saved report"
                  aria-label={`Delete saved ANRE report from ${formatTimestamp(report.createdAt)}`}
                  onClick={() => onDelete(report)}
                  disabled={deletingReportId === report.id}
                  className="inline-flex h-7 w-7 items-center justify-center rounded border border-border text-muted hover:border-negRed hover:text-negRed disabled:opacity-50"
                >
                  <X size={14} />
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DefaultTemplateStatus({
  template,
  uploadingOverride,
  loading,
  downloading,
  onDownload,
}: {
  template: SavedAnreTemplate | null;
  uploadingOverride: boolean;
  loading: boolean;
  downloading: boolean;
  onDownload: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
      <span className="font-mono text-fg">
        Default template: {loading ? "checking..." : template ? template.filename : "none"}
      </span>
      {template && (
        <>
          <span>{formatBytes(template.sizeBytes)}</span>
          <span>{formatTimestamp(template.uploadedAt)}</span>
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] hover:border-accent hover:text-accent disabled:opacity-50"
          >
            <Download size={12} />
            Template
          </button>
        </>
      )}
      {!loading && !template && !uploadingOverride && <span>Upload a template once to enable template-free runs.</span>}
      {uploadingOverride && <span className="text-accent">New upload will become default.</span>}
    </div>
  );
}

function AnreStatusBadge({ status, compact = false }: { status: AnreComparison["status"]; compact?: boolean }) {
  const ok = status === "ok";
  const info = status === "info";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs",
        ok
          ? "border-posGreen/40 bg-posGreen/10 text-posGreen"
          : info
          ? "border-accent/40 bg-accent/10 text-accent"
          : "border-warnOrange/50 bg-warnOrange/10 text-warnOrange"
      )}
    >
      {ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
      {compact
        ? ok
          ? "OK"
          : info
          ? "Value basis"
          : "Mismatch"
        : ok
        ? "Matches internal records"
        : info
        ? "Volumes match; value basis differs"
        : "Differs from internal records"}
    </span>
  );
}

function FileField({
  label,
  file,
  onChange,
  onRejected,
  accept,
  panelActive = false,
  onDropComplete,
}: {
  label: string;
  file: File | null;
  onChange: (file: File | null) => void;
  onRejected?: (file: File) => void;
  accept: string;
  panelActive?: boolean;
  onDropComplete?: () => void;
}) {
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  const active = dragActive || panelActive;

  function applyFile(nextFile: File | null) {
    if (!nextFile) {
      onChange(null);
      return;
    }
    if (!fileMatchesAccept(nextFile, accept)) {
      onRejected?.(nextFile);
      return;
    }
    onChange(nextFile);
  }

  function handleDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setDragActive(false);
    onDropComplete?.();
    applyFile(e.dataTransfer.files[0] ?? null);
  }

  return (
    <label
      className={cn(
        "text-xs text-muted flex min-w-[15rem] max-w-[22rem] cursor-pointer flex-col gap-1 rounded border border-dashed border-border px-3 py-2 transition-all duration-150",
        active && "border-accent bg-accent/15 text-accent ring-2 ring-accent/30"
      )}
      onDragEnter={(e) => {
        e.preventDefault();
        if (!isFileDrag(e.dataTransfer)) return;
        dragDepthRef.current += 1;
        setDragActive(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (!isFileDrag(e.dataTransfer)) return;
        e.dataTransfer.dropEffect = "copy";
        setDragActive(true);
      }}
      onDragLeave={(e) => {
        if (!isFileDrag(e.dataTransfer)) return;
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setDragActive(false);
      }}
      onDrop={handleDrop}
    >
      <span>{label}</span>
      <input
        type="file"
        accept={accept}
        onChange={(e) => applyFile(e.target.files?.[0] ?? null)}
        className="sr-only"
      />
      <span className="truncate font-mono text-[10px] text-fg">{file?.name ?? "No file selected"}</span>
    </label>
  );
}

function DocumentTypeSelect({
  value,
  onChange,
}: {
  value: ReportDocumentType;
  onChange: (value: ReportDocumentType) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as ReportDocumentType)}
      className="bg-transparent border border-border rounded px-2 py-1 text-sm"
    >
      <DocumentTypeOptions />
    </select>
  );
}

function DocumentTypeOptions() {
  return (
    <>
      <option value="invoice">Invoice</option>
      <option value="settlement">Settlement</option>
      <option value="centralizer">Centralizer</option>
      <option value="contract">Contract</option>
      <option value="supporting">Supporting</option>
      <option value="other">Other</option>
    </>
  );
}

function DocumentMarketSelect({
  value,
  onChange,
}: {
  value: "" | ReportDocumentMarket;
  onChange: (value: "" | ReportDocumentMarket) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as "" | ReportDocumentMarket)}
      className="bg-transparent border border-border rounded px-2 py-1 text-sm"
    >
      <option value="">None</option>
      <option value="BRMDA">BRMDA</option>
      <option value="BRMID">BRMID</option>
      <option value="BALANCING">Balancing</option>
      <option value="OTHER">Other</option>
    </select>
  );
}

function DocumentCheckBadge({ status }: { status: ReportDocumentCrossCheck["status"] }) {
  const ok = status === "ok";
  const info = status === "info";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs",
        ok
          ? "border-posGreen/40 bg-posGreen/10 text-posGreen"
          : info
          ? "border-accent/40 bg-accent/10 text-accent"
          : "border-warnOrange/50 bg-warnOrange/10 text-warnOrange"
      )}
    >
      {ok ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
      {ok ? "Matched" : info ? "Open" : "Mismatch"}
    </span>
  );
}

function AnreSettlementBreakdownTable({ summary }: { summary: AnreSummary }) {
  if (!summary.brmda.breakdown || !summary.brmid.breakdown) {
    return <AnreAggregateRowsTable summary={summary} />;
  }

  return (
    <div className="overflow-auto border border-border/40 rounded">
      <table className="font-mono text-[11px] min-w-[72rem] w-full">
        <thead className="text-muted">
          <tr>
            <Th>Market</Th>
            <Th>Centralizer row</Th>
            <Th className="text-right">MWh</Th>
            <Th className="text-right">lei/MWh</Th>
            <Th className="text-right">lei fara TVA</Th>
            <Th className="text-right">TVA</Th>
            <Th className="text-right">lei cu TVA</Th>
          </tr>
        </thead>
        <tbody>
          {settlementBreakdownRows(summary).map((row) => (
            <tr key={`${row.market}:${row.label}`} className={cn("border-t border-border/30", row.tone)}>
              <Td>{row.market}</Td>
              <Td>{row.label}</Td>
              <Td className="text-right">{formatMw(row.leg.quantityMWh)}</Td>
              <Td className="text-right">{formatPrice(row.leg.priceRonMWh)}</Td>
              <Td className="text-right">{formatPrice(row.leg.valueRon)}</Td>
              <Td className="text-right">{formatPrice(row.leg.vatRon)}</Td>
              <Td className="text-right">{formatPrice(row.leg.totalRon)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnreAggregateRowsTable({ summary }: { summary: AnreSummary }) {
  return (
    <div className="overflow-auto">
      <table className="font-mono text-[11px] min-w-[42rem]">
        <thead className="text-muted">
          <tr>
            <Th>ANRE row</Th>
            <Th className="text-right">MWh</Th>
            <Th className="text-right">lei</Th>
            <Th className="text-right">lei/MWh</Th>
          </tr>
        </thead>
        <tbody>
          <AnreSummaryRow label="Cumparare pe PZU - BRM" leg={summary.brmda.buy} />
          <AnreSummaryRow label="Vanzare pe PZU - BRM" leg={summary.brmda.sell} />
          <AnreSummaryRow label="Cumparare pe PI-BRM (IDCT)" leg={summary.brmid.buy} />
          <AnreSummaryRow label="Vanzare pe PI-BRM (IDCT)" leg={summary.brmid.sell} />
        </tbody>
      </table>
    </div>
  );
}

function settlementBreakdownRows(summary: AnreSummary) {
  const da = summary.brmda.breakdown!;
  const id = summary.brmid.breakdown!;
  return [
    { market: "DA", label: "Cumparare la pret pozitiv", leg: da.buyPositive, tone: "text-posGreen" },
    { market: "DA", label: "Vanzare la pret pozitiv", leg: da.sellPositive, tone: "text-posGreen" },
    { market: "DA", label: "Cumparare la pret negativ", leg: da.buyNegative, tone: "text-warnOrange" },
    { market: "DA", label: "Vanzare la pret negativ", leg: da.sellNegative, tone: "text-warnOrange" },
    { market: "IDM", label: "Cumparare la pret pozitiv", leg: id.buyPositive, tone: "text-posGreen" },
    { market: "IDM", label: "Vanzare la pret pozitiv", leg: id.sellPositive, tone: "text-posGreen" },
    { market: "IDM", label: "Cumparare la pret negativ", leg: id.buyNegative, tone: "text-warnOrange" },
    { market: "IDM", label: "Vanzare la pret negativ", leg: id.sellNegative, tone: "text-warnOrange" },
  ];
}

function AnreSummaryRow({ label, leg }: { label: string; leg: AnreLeg }) {
  return (
    <tr className="border-t border-border/30">
      <Td>{label}</Td>
      <Td className="text-right">{formatMw(leg.quantityMWh)}</Td>
      <Td className="text-right">{formatPrice(leg.valueRon)}</Td>
      <Td className="text-right">{formatPrice(leg.priceRonMWh)}</Td>
    </tr>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="text-xs text-muted flex flex-col gap-1">
      {label}
      {children}
    </label>
  );
}

function PortfolioSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Portfolio[];
  placeholder: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-transparent border border-border rounded px-2 py-1 text-sm min-w-[17rem]"
    >
      <option value="">{placeholder}</option>
      {options.map((p) => (
        <option key={p.id} value={p.id}>
          {portfolioLabel(p)}
        </option>
      ))}
    </select>
  );
}

function portfolioLabel(p: Portfolio): string {
  const owner = p.ownerDisplayName ?? p.ownerEmail ?? "?";
  return `${owner} · ${p.market} · ${p.brokerCode}${p.shortName ? ` · ${p.shortName}` : ""}`;
}

function DailyReportView({ report }: { report: DailyReport }) {
  const daDirection = direction(report.summary.da.netMwh);
  const idDirection = direction(report.summary.id.netMwh);
  const netDirection = direction(report.summary.netMwh);
  const bmPnl = report.summary.balancing.pnlEur;
  return (
    <div className="space-y-4">
      <section className="border border-border/60 rounded p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <div className="text-xs text-muted uppercase tracking-wider">Daily trading report</div>
            <div className="text-lg font-semibold">{report.deliveryDate}</div>
            <div className="text-xs text-muted">
              {report.portfolios.da ?? "All DA portfolios"} / {report.portfolios.id ?? "All ID portfolios"}
            </div>
          </div>
          <div className="ml-auto text-xs text-muted text-right">
            <div>
              FX EUR/RON{" "}
              <span className="text-fg font-mono">
                {report.fx.rate > 0 ? report.fx.rate.toFixed(4) : "missing"}
              </span>
            </div>
            <div>{report.fx.asOfDate ? `fixing ${report.fx.asOfDate}` : report.fx.source}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-[11px] border-y border-border/40 py-2">
          <CoverageBadge ok={report.coverage.daSource !== "none"} label={`DA: ${sourceLabel(report.coverage.daSource)}`} />
          <CoverageBadge ok={report.coverage.idRows > 0} label={`ID: ${report.coverage.idRows} grouped rows`} />
          <CoverageBadge
            ok={report.coverage.balancingRows > 0}
            label={`Balancing: ${report.coverage.finalBalancingRows} final / ${report.coverage.estimatedBalancingRows} estimated`}
          />
          <CoverageBadge ok={report.coverage.fxAvailable} label={report.fx.asOfDate ? `FX: ${report.fx.asOfDate}` : "FX"} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <ReportBlock
            step="DA"
            title="Day-ahead gross and net"
            tone={daDirection.tone}
            main={`${daDirection.label} ${formatMw(Math.abs(report.summary.da.netMwh))} MWh net`}
          >
            <MetricTable
              rows={[
                {
                  label: "Buy",
                  qty: mwhLabel(report.summary.da.buy.mwh),
                  price: (
                    <StackedValue
                      primary={priceUnitLabel(report.summary.da.buy.vwapRon, "RON")}
                      secondary={priceUnitLabel(report.summary.da.buy.vwapEur, "EUR")}
                    />
                  ),
                  value: (
                    <StackedValue
                      primary={moneyLabel(report.summary.da.buy.valueRon, "RON")}
                      secondary={moneyLabel(report.summary.da.buy.valueEur, "EUR")}
                    />
                  ),
                  tone: "pos",
                },
                {
                  label: "Sell",
                  qty: mwhLabel(report.summary.da.sell.mwh),
                  price: (
                    <StackedValue
                      primary={priceUnitLabel(report.summary.da.sell.vwapRon, "RON")}
                      secondary={priceUnitLabel(report.summary.da.sell.vwapEur, "EUR")}
                    />
                  ),
                  value: (
                    <StackedValue
                      primary={moneyLabel(report.summary.da.sell.valueRon, "RON")}
                      secondary={moneyLabel(report.summary.da.sell.valueEur, "EUR")}
                    />
                  ),
                  tone: "neg",
                },
                {
                  label: "Net",
                  qty: signedMwhLabel(report.summary.da.netMwh),
                  price: (
                    <StackedValue
                      primary={priceUnitLabel(unitPrice(report.summary.da.cashflowRon, report.summary.da.netMwh), "RON")}
                      secondary={priceUnitLabel(unitPrice(report.summary.da.cashflowEur, report.summary.da.netMwh), "EUR")}
                    />
                  ),
                  value: (
                    <StackedValue
                      primary={cashflowLabel(report.summary.da.cashflowRon, "RON")}
                      secondary={cashflowLabel(report.summary.da.cashflowEur, "EUR")}
                    />
                  ),
                  tone: daDirection.tone,
                },
              ]}
            />
            <MetaLines items={[["Source", sourceLabel(report.coverage.daSource)]]} />
          </ReportBlock>
          <ReportBlock
            step="ID"
            title="Intraday gross and net"
            tone={idDirection.tone}
            main={`${idDirection.label} ${formatMw(Math.abs(report.summary.id.netMwh))} MWh net`}
          >
            <MetricTable
              rows={[
                {
                  label: "Buy",
                  qty: mwhLabel(report.summary.id.buy.mwh),
                  price: priceUnitLabel(report.summary.id.buy.vwapEur, "EUR"),
                  value: moneyLabel(report.summary.id.buy.valueEur, "EUR"),
                  tone: "pos",
                },
                {
                  label: "Sell",
                  qty: mwhLabel(report.summary.id.sell.mwh),
                  price: priceUnitLabel(report.summary.id.sell.vwapEur, "EUR"),
                  value: moneyLabel(report.summary.id.sell.valueEur, "EUR"),
                  tone: "neg",
                },
                {
                  label: "Net",
                  qty: signedMwhLabel(report.summary.id.netMwh),
                  price: priceUnitLabel(unitPrice(report.summary.id.cashflowEur, report.summary.id.netMwh), "EUR"),
                  value: cashflowLabel(report.summary.id.cashflowEur, "EUR"),
                  tone: idDirection.tone,
                },
              ]}
            />
            <MetaLines items={[["Grouped rows", `${report.coverage.idRows}`]]} />
          </ReportBlock>
          <ReportBlock
            step="NET"
            title="Net market position"
            tone={netDirection.tone}
            main={`${netDirection.label} ${formatMw(Math.abs(report.summary.netMwh))} MWh`}
          >
            <MetricTable
              columns={["Qty", "QHs", "Status"]}
              rows={[
                {
                  label: "DA",
                  qty: signedMwhLabel(report.summary.da.netMwh),
                  price: "base",
                  value: sourceLabel(report.coverage.daSource),
                  tone: direction(report.summary.da.netMwh).tone,
                },
                {
                  label: "ID",
                  qty: signedMwhLabel(report.summary.id.netMwh),
                  price: `${report.coverage.idRows} rows`,
                  value: "intraday",
                  tone: direction(report.summary.id.netMwh).tone,
                },
                {
                  label: "Net",
                  qty: signedMwhLabel(report.summary.netMwh),
                  price: `${report.summary.openQhCount} open / ${report.summary.flatQhCount} flat`,
                  value: report.summary.openQhCount > 0 ? "BM exposure" : "flat",
                  tone: netDirection.tone,
                },
              ]}
            />
            <MetaLines items={[["BM exposure", mwhLabel(report.summary.bmExposureMwh)]]} />
          </ReportBlock>
          <ReportBlock
            step="BM"
            title="Balancing long and short"
            tone={bmPnl !== null && bmPnl < 0 ? "neg" : bmPnl !== null && bmPnl > 0 ? "pos" : "flat"}
            main={`${signedMoneyLabel(bmPnl, "EUR")} P&L`}
          >
            <MetricTable
              columns={["Qty", "Price", "P&L"]}
              rows={[
                {
                  label: "Long",
                  qty: (
                    <StackedValue
                      primary={mwhLabel(report.summary.balancing.long.mwh)}
                      secondary={`${report.summary.balancing.long.intervals} QHs`}
                    />
                  ),
                  price: priceUnitLabel(report.summary.balancing.long.avgPriceEur, "EUR"),
                  value: signedMoneyLabel(report.summary.balancing.long.pnlEur, "EUR"),
                  tone: "pos",
                },
                {
                  label: "Short",
                  qty: (
                    <StackedValue
                      primary={mwhLabel(report.summary.balancing.short.mwh)}
                      secondary={`${report.summary.balancing.short.intervals} QHs`}
                    />
                  ),
                  price: priceUnitLabel(report.summary.balancing.short.avgPriceEur, "EUR"),
                  value: signedMoneyLabel(report.summary.balancing.short.pnlEur, "EUR"),
                  tone: "neg",
                },
                {
                  label: "Net",
                  qty: signedMwhLabel(report.summary.balancing.netMwh),
                  price: `${report.coverage.finalBalancingRows} final / ${report.coverage.estimatedBalancingRows} est.`,
                  value: signedMoneyLabel(report.summary.balancing.pnlEur, "EUR"),
                  tone: bmPnl !== null && bmPnl < 0 ? "neg" : bmPnl !== null && bmPnl > 0 ? "pos" : "flat",
                },
              ]}
            />
            <MetaLines
              items={[
                ["Cashflow", signedMoneyLabel(report.summary.balancing.cashflowEur, "EUR")],
                ["Prices", `${report.coverage.finalBalancingRows} final / ${report.coverage.estimatedBalancingRows} estimated`],
              ]}
            />
          </ReportBlock>
        </div>
      </section>

      <section className="overflow-auto border border-border/60 rounded-md max-h-[34rem]">
        <table className="min-w-full font-mono text-[11px]">
          <thead className="bg-bg sticky top-0">
            <tr className="text-muted">
              <Th>QH</Th>
              <Th>Flow</Th>
              <Th className="text-right">DA Buy MW</Th>
              <Th className="text-right">DA Buy RON</Th>
              <Th className="text-right">DA Sell MW</Th>
              <Th className="text-right">DA Sell RON</Th>
              <Th className="text-right">DA Net MW</Th>
              <Th className="text-right">ID Buy MW</Th>
              <Th className="text-right">ID Buy EUR</Th>
              <Th className="text-right">ID Sell MW</Th>
              <Th className="text-right">ID Sell EUR</Th>
              <Th className="text-right">Net MW</Th>
              <Th>BM Source</Th>
              <Th className="text-right">BM EUR/MWh</Th>
              <Th className="text-right">Position EUR/MWh</Th>
              <Th className="text-right">BM P&L</Th>
              <Th>Data</Th>
            </tr>
          </thead>
          <tbody>
            {report.grid.map((r) => (
              <tr key={r.qhIndex} className="border-t border-border/30 hover:bg-border/10">
                <Td>QH-{r.qhIndex}</Td>
                <Td className={signClass(r.netMw)}>{direction(r.netMw).label}</Td>
                <Td className={cn("text-right", signClass(r.daBuyMw))}>{formatMw(r.daBuyMw)}</Td>
                <Td className="text-right">{formatPrice(r.daBuyVwapRon)}</Td>
                <Td className={cn("text-right", signClass(r.daSellMw))}>{formatMw(Math.abs(r.daSellMw))}</Td>
                <Td className="text-right">{formatPrice(r.daSellVwapRon)}</Td>
                <Td className={cn("text-right", signClass(r.daBaseMw))}>{formatMw(r.daBaseMw)}</Td>
                <Td className={cn("text-right", signClass(r.idBuyMw))}>{formatMw(r.idBuyMw)}</Td>
                <Td className="text-right">{formatPrice(r.idBuyVwapEur)}</Td>
                <Td className={cn("text-right", signClass(r.idSellMw))}>{formatMw(Math.abs(r.idSellMw))}</Td>
                <Td className="text-right">{formatPrice(r.idSellVwapEur)}</Td>
                <Td className={cn("text-right font-semibold", signClass(r.netMw))}>{formatMw(r.netMw)}</Td>
                <Td>{r.bmPriceSource}</Td>
                <Td className="text-right">{formatPrice(r.bmAppliedPriceEur)}</Td>
                <Td className="text-right text-muted">{formatPrice(r.bmPositionPriceEur)}</Td>
                <Td className={cn("text-right", signClass(r.bmPnlEur))}>{formatPrice(r.bmPnlEur)}</Td>
                <Td className={dataStatusClass(r.bmDataStatus)}>{r.bmDataStatus}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function ReportBlock({
  step,
  title,
  main,
  tone,
  children,
}: {
  step: string;
  title: string;
  main: string;
  tone: Tone;
  children: React.ReactNode;
}) {
  const toneCls =
    tone === "pos"
      ? "border-posGreen/35 bg-posGreen/5"
      : tone === "neg"
      ? "border-negRed/35 bg-negRed/5"
      : "border-border/60 bg-border/10";
  return (
    <div className={cn("border rounded p-3 space-y-2 min-w-0", toneCls)}>
      <div className="flex items-center gap-2">
        <span className="inline-flex h-5 min-w-7 items-center justify-center rounded border border-current/30 px-1 text-[10px] font-semibold">
          {step}
        </span>
        <span className="text-muted text-[10px] uppercase tracking-wider">{title}</span>
      </div>
      <div className={cn("font-mono text-base font-semibold", toneText(tone))}>{main}</div>
      {children}
    </div>
  );
}

type Tone = "pos" | "neg" | "flat";

interface MetricTableRow {
  label: string;
  qty: React.ReactNode;
  price: React.ReactNode;
  value: React.ReactNode;
  tone?: Tone;
}

function MetricTable({
  columns = ["Qty", "Price", "Value"],
  rows,
}: {
  columns?: [string, string, string];
  rows: MetricTableRow[];
}) {
  return (
    <table className="w-full table-fixed font-mono text-xs">
      <thead>
        <tr className="text-[10px] uppercase text-muted">
          <th className="w-[4.25rem] py-1 pr-2 text-left font-normal"></th>
          <th className="py-1 px-2 text-right font-normal">{columns[0]}</th>
          <th className="py-1 px-2 text-right font-normal">{columns[1]}</th>
          <th className="py-1 pl-2 text-right font-normal">{columns[2]}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label} className="border-t border-border/25 first:border-t-0">
            <th className="py-1.5 pr-2 text-left font-normal text-muted">{row.label}</th>
            <td className={cn("py-1.5 px-2 text-right align-top", row.tone ? toneText(row.tone) : "")}>{row.qty}</td>
            <td className="py-1.5 px-2 text-right align-top">{row.price}</td>
            <td className={cn("py-1.5 pl-2 text-right align-top", row.tone ? toneText(row.tone) : "")}>{row.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MetaLines({ items }: { items: [string, string][] }) {
  return (
    <div className="border-t border-border/30 pt-2 space-y-1 text-xs">
      {items.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between gap-3">
          <span className="text-muted">{label}</span>
          <span className="font-mono text-right">{value}</span>
        </div>
      ))}
    </div>
  );
}

function StackedValue({ primary, secondary }: { primary: string; secondary?: string }) {
  return (
    <span className="block leading-tight">
      <span className="block">{primary}</span>
      {secondary && <span className="block text-[10px] text-muted">{secondary}</span>}
    </span>
  );
}

function CoverageBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={cn(
        "inline-flex rounded border px-2 py-0.5",
        ok
          ? "border-posGreen/40 bg-posGreen/10 text-posGreen"
          : "border-warnOrange/50 bg-warnOrange/10 text-warnOrange"
      )}
    >
      {label}
    </span>
  );
}

function dataStatusClass(status: DailyGridRow["bmDataStatus"]): string {
  return status === "missing"
    ? "text-negRed"
    : status === "partial"
    ? "text-warnOrange"
    : status === "final"
    ? "text-posGreen"
    : "text-muted";
}

function direction(value: number): { label: string; tone: "pos" | "neg" | "flat" } {
  if (value > 1e-9) return { label: "BUY", tone: "pos" };
  if (value < -1e-9) return { label: "SELL", tone: "neg" };
  return { label: "FLAT", tone: "flat" };
}

function toneText(tone: "pos" | "neg" | "flat"): string {
  return tone === "pos" ? "text-posGreen" : tone === "neg" ? "text-negRed" : "";
}

function mwhLabel(value: number | null | undefined): string {
  return `${formatMw(value)} MWh`;
}

function signedMwhLabel(value: number): string {
  if (Math.abs(value) < 1e-9) return "0.0 MWh";
  return `${value > 0 ? "+" : "-"}${formatMw(Math.abs(value))} MWh`;
}

function moneyLabel(value: number | null | undefined, currency: string): string {
  return `${formatPrice(value)} ${currency}`;
}

function priceUnitLabel(value: number | null | undefined, currency: string): string {
  return `${formatPrice(value)} ${currency}/MWh`;
}

function unitPrice(value: number | null, qtyMwh: number): number | null {
  if (value === null || Math.abs(qtyMwh) < 1e-9) return null;
  return Math.abs(value) / Math.abs(qtyMwh);
}

function cashDirection(value: number | null): string {
  if (value === null || Math.abs(value) < 1e-9) return "flat";
  return value > 0 ? "receive" : "pay";
}

function cashflowLabel(value: number | null, currency: string): string {
  if (value === null) return `— ${currency}`;
  if (Math.abs(value) < 1e-9) return `0.00 ${currency} flat`;
  return `${formatPrice(Math.abs(value))} ${currency} ${cashDirection(value)}`;
}

function dualCashflowLabel(
  primary: number | null,
  primaryCurrency: string,
  secondary: number | null,
  secondaryCurrency: string
): string {
  if (primary === null && secondary === null) return `— ${primaryCurrency}`;
  const directionLabel = cashDirection(primary ?? secondary);
  const primaryAmount = primary === null ? `— ${primaryCurrency}` : `${formatPrice(Math.abs(primary))} ${primaryCurrency}`;
  const secondaryAmount =
    secondary === null ? `— ${secondaryCurrency}` : `${formatPrice(Math.abs(secondary))} ${secondaryCurrency}`;
  return `${primaryAmount} / ${secondaryAmount} ${directionLabel}`;
}

function signedMoneyLabel(value: number | null, currency: string): string {
  if (value === null) return `— ${currency}`;
  if (Math.abs(value) < 1e-9) return `0.00 ${currency}`;
  return `${value > 0 ? "+" : "-"}${formatPrice(Math.abs(value))} ${currency}`;
}

function sourceLabel(source: DailyReport["coverage"]["daSource"]): string {
  if (source === "schedule") return "confirmed schedule";
  if (source === "brmda_trades") return "BRMDA trades";
  return "missing";
}

function sourceShortLabel(source: AnreComparisonRow["source"]): string {
  if (source === "dam_schedule") return "DA schedule";
  if (source === "brmda_internal") return "Trades tab DA";
  if (source === "brmda_trades") return "BRMDA trades";
  if (source === "brmid_trades") return "BRMID trades";
  return "missing";
}

function artifactLabel(artifact: "report" | "template" | "brmda" | "brmid"): string {
  if (artifact === "report") return "Final";
  if (artifact === "template") return "Template";
  if (artifact === "brmda") return "BRMDA";
  return "BRMID";
}

function availableAnreArtifacts(report: SavedAnreReport): Array<"report" | "template" | "brmda" | "brmid"> {
  const artifacts: Array<"report" | "template" | "brmda" | "brmid"> = ["report", "template"];
  if (report.brmdaFilename !== null) artifacts.push("brmda");
  if (report.brmidFilename !== null) artifacts.push("brmid");
  return artifacts;
}

function optionalFileSize(sizeBytes: number | null): string {
  return sizeBytes === null ? "—" : formatBytes(sizeBytes);
}

function defaultReportPeriod(): { year: number; month: number } {
  const parts = todayBucharestIso().split("-").map(Number);
  const year = parts[0];
  const month = parts[1];
  return {
    year: typeof year === "number" && Number.isFinite(year) ? year : new Date().getFullYear(),
    month: typeof month === "number" && Number.isFinite(month) ? month : new Date().getMonth() + 1,
  };
}

function reportMonthFromParts(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function reportMonthLabel(value: string | null | undefined): string {
  if (!value) return "—";
  const [yearRaw, monthRaw] = value.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const monthLabel = MONTH_OPTIONS.find((option) => option.value === month)?.label;
  return monthLabel && Number.isFinite(year) ? `${monthLabel} ${year}` : "—";
}

function reportYearOptions(selectedYear: number): number[] {
  const currentYear = defaultReportPeriod().year;
  const years = new Set<number>([selectedYear]);
  for (let year = currentYear + 1; year >= currentYear - 4; year--) years.add(year);
  return [...years].sort((a, b) => b - a);
}

function documentTypeLabel(type: ReportDocumentType): string {
  if (type === "invoice") return "Invoice";
  if (type === "settlement") return "Settlement";
  if (type === "centralizer") return "Centralizer";
  if (type === "contract") return "Contract";
  if (type === "supporting") return "Supporting";
  return "Other";
}

function flowLabel(flow: ReportDocumentFlow): string {
  if (flow === "payable") return "Payable";
  if (flow === "receivable") return "Receivable";
  return "Neutral";
}

function amountBasisLabel(basis: ReportDocumentAmountBasis): string {
  if (basis === "without_vat") return "without VAT";
  if (basis === "with_vat") return "with VAT";
  return "basis unknown";
}

function documentExtractionSourceLabel(source: ReportDocumentExtraction["source"]): string {
  if (source === "xml") return "XML invoice";
  if (source === "spreadsheet") return "Spreadsheet";
  if (source === "pdf") return "PDF text";
  if (source === "text") return "Text";
  if (source === "filename") return "Filename";
  return "Stored file";
}

function documentExtractionSummary(fields: ReportDocumentExtractedFields): string {
  const parts: string[] = [];
  if (fields.market || fields.flow) parts.push(`${fields.market ?? "Market?"} ${fields.flow ? flowLabel(fields.flow) : ""}`.trim());
  if (fields.counterparty) parts.push(fields.counterparty);
  if (fields.invoiceNumber) parts.push(`Invoice ${fields.invoiceNumber}`);
  if (fields.issueDate) parts.push(fields.issueDate);
  if (fields.amount !== undefined) {
    const amount = `${formatPrice(fields.amount)} ${fields.currency ?? ""}`.trim();
    parts.push(`${amount} ${fields.amountBasis ? amountBasisLabel(fields.amountBasis) : ""}`.trim());
  }
  return parts.join(" · ") || "No invoice fields found";
}

function documentCheckClass(status: ReportDocumentCheckRow["status"]): string {
  if (status === "warning") return "text-warnOrange";
  if (status === "info") return "text-accent";
  return "text-posGreen";
}

type AnreDropSlot = "template" | "brmda" | "brmid";

function classifyAnreDropFile(file: File): AnreDropSlot | null {
  const name = normaliseFilename(file.name);
  if (name.includes("brmda")) return "brmda";
  if (name.includes("brmid")) return "brmid";
  if (name.includes("anre") || name.includes("monit") || (name.endsWith(".xls") && !name.endsWith(".xlsx"))) {
    return "template";
  }
  return null;
}

function fileMatchesAccept(file: File, accept: string): boolean {
  const name = normaliseFilename(file.name);
  return accept
    .split(",")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)
    .some((part) => {
      if (part.startsWith(".")) return name.endsWith(part);
      if (part.endsWith("/*")) return file.type.startsWith(part.slice(0, -1));
      return file.type === part;
    });
}

function isFileDrag(dataTransfer: DataTransfer): boolean {
  return Array.from(dataTransfer.types).includes("Files");
}

function normaliseFilename(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function diffClass(value: number | null): string {
  if (value === null || Math.abs(value) < 1e-9) return "";
  return value > 0 ? "text-posGreen" : "text-negRed";
}

function comparisonStatusClass(status: AnreComparisonRow["status"]): string {
  if (status === "warning") return "text-warnOrange";
  if (status === "info") return "text-accent";
  return "text-posGreen";
}

function formatSignedNumber(value: number | null): string {
  if (value === null) return "—";
  if (Math.abs(value) < 1e-9) return "0.000";
  return `${value > 0 ? "+" : "-"}${formatPrice(Math.abs(value))}`;
}

function summaryOnlySavedReport(summary: AnreSummary): SavedAnreReport {
  const comparison: AnreComparison = {
    status: "ok",
    participant: { report: summary.participant, internalCompany: "", status: "ok" },
    rows: [],
  };
  return {
    id: "",
    participant: summary.participant,
    monthLabel: summary.monthLabel,
    year: summary.year,
    reportMonth: 0,
    resultFilename: "anre-monthly-filled.xls",
    resultSizeBytes: 0,
    templateFilename: "",
    templateSizeBytes: 0,
    brmdaFilename: summary.centralizers?.brmda === false ? null : "",
    brmdaSizeBytes: summary.centralizers?.brmda === false ? null : 0,
    brmidFilename: summary.centralizers?.brmid === false ? null : "",
    brmidSizeBytes: summary.centralizers?.brmid === false ? null : 0,
    summary,
    comparison,
    createdAt: new Date().toISOString(),
    createdByEmail: null,
    createdByDisplayName: null,
  };
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1] ?? null;
}

function activeCompanyHeaders(): HeadersInit | undefined {
  const activeCompanyId = selectedCompanyId();
  return activeCompanyId ? { "x-company-id": activeCompanyId } : undefined;
}

function parseBase64JsonHeader<T>(header: string | null): T | null {
  if (!header) return null;
  try {
    const base64 = header.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    return JSON.parse(atob(padded)) as T;
  } catch {
    return null;
  }
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn("px-2 py-1.5 font-normal whitespace-nowrap text-left", className)}>{children}</th>;
}

function Td({ children, className, colSpan }: { children: React.ReactNode; className?: string; colSpan?: number }) {
  return (
    <td colSpan={colSpan} className={cn("px-2 py-1 whitespace-nowrap", className)}>
      {children}
    </td>
  );
}
